import { isSuccess } from '@yimoka/shared';
import { rootStore } from '@yimoka/store';

import { getEnv } from '@/env';
import { platformHttpGet } from '@/http';
import { getCurrentLanguage } from '@/locales/language';

import type { RealmProfileSource } from './context';
import type { IRealmCurrentReply, IRealmProfileSnapshot } from './types';
import { parseRealmCurrentReply } from './validation';

const PROFILE_API = '/base/iam/portal/realm/current';
const REALM_PROFILE_STORE_KEY = 'tenant-web:realm-profile';
const DEFAULT_REALM = 'default';

export interface IRealmProfileState {
  error?: string;
  loading: boolean;
  locale: string;
  profile?: IRealmProfileSnapshot;
  requestedRealm: string;
  source: RealmProfileSource;
}

let activeRequest = 0;
let inFlight: Promise<IRealmProfileState> | undefined;
let inFlightKey = '';

/** createInitialRealmProfileState 为 AppShell 提供不包含硬编码页面内容的初始状态。 */
function createInitialRealmProfileState(requestedRealm: string, locale = getCurrentLanguage()): IRealmProfileState {
  return {
    // 首次提交就进入加载态，避免 useEffect 发起请求前短暂闪现“渠道不可用”。
    loading: true,
    locale,
    requestedRealm,
    source: 'unavailable',
  };
}

/** setRealmProfileState 把渠道完整快照集中写入应用 rootStore。 */
function setRealmProfileState(state: IRealmProfileState): IRealmProfileState {
  rootStore.setDataItem(REALM_PROFILE_STORE_KEY, state);
  return state;
}

/** getRealmProfileState 从 rootStore 读取当前站点渠道配置。 */
export function getRealmProfileState(requestedRealm = getEnv().realm): IRealmProfileState {
  const state = rootStore.getDataItem(REALM_PROFILE_STORE_KEY) as IRealmProfileState | undefined;
  const locale = getCurrentLanguage();
  return state?.requestedRealm === requestedRealm && state.locale === locale
    ? state
    : createInitialRealmProfileState(requestedRealm, locale);
}

/** fetchPublishedRealmProfile 读取并适配一个 Realm 的当前发布版本。 */
async function fetchPublishedRealmProfile(realmName: string, locale: string): Promise<IRealmProfileSnapshot | undefined> {
  const response = await platformHttpGet<IRealmCurrentReply, { name: string }>(PROFILE_API, {
    headers: { 'x-md-global-realm': realmName },
    params: { name: realmName },
  });
  if (!isSuccess(response)) return undefined;
  return parseRealmCurrentReply(response.data, realmName, locale);
}

interface IRealmProfileFetchResult {
  failed: boolean;
  profile?: IRealmProfileSnapshot;
}

/** tryFetchPublishedRealmProfile 把单个 Realm 的网络失败收敛为结果，避免阻断 default 回退。 */
async function tryFetchPublishedRealmProfile(realmName: string, locale: string): Promise<IRealmProfileFetchResult> {
  try {
    return { failed: false, profile: await fetchPublishedRealmProfile(realmName, locale) };
  } catch {
    return { failed: true };
  }
}

/** resolveRealmProfile 优先使用站点 Realm，缺失或无有效发布版本时读取远端 default Realm。 */
async function resolveRealmProfile(requestedRealm: string, locale: string): Promise<IRealmProfileState> {
  const published = await tryFetchPublishedRealmProfile(requestedRealm, locale);
  if (published.profile) {
    return { loading: false, locale, profile: published.profile, requestedRealm, source: 'published' };
  }

  let requestFailed = published.failed;
  if (requestedRealm !== DEFAULT_REALM) {
    const fallback = await tryFetchPublishedRealmProfile(DEFAULT_REALM, locale);
    requestFailed ||= fallback.failed;
    if (fallback.profile) {
      return { loading: false, locale, profile: fallback.profile, requestedRealm, source: 'default' };
    }
  }

  return {
    error: requestFailed ? 'realm-profile-request-failed' : 'realm-profile-unavailable',
    loading: false,
    locale,
    requestedRealm,
    source: 'unavailable',
  };
}

/** loadRealmProfile 在单一应用入口内去重加载渠道配置，并忽略过期请求结果。 */
export function loadRealmProfile(requestedRealm = getEnv().realm, force = false): Promise<IRealmProfileState> {
  const locale = getCurrentLanguage();
  const requestKey = `${requestedRealm}:${locale}`;
  const current = getRealmProfileState(requestedRealm);
  if (!force && current.profile) return Promise.resolve(current);
  if (!force && inFlight && inFlightKey === requestKey && current.loading) return inFlight;

  activeRequest += 1;
  const requestID = activeRequest;
  inFlightKey = requestKey;
  setRealmProfileState({ ...current, error: undefined, loading: true, locale, requestedRealm });
  inFlight = resolveRealmProfile(requestedRealm, locale)
    .then((state) => {
      if (requestID === activeRequest) setRealmProfileState(state);
      return state;
    })
    .finally(() => {
      if (requestID === activeRequest) inFlight = undefined;
      if (requestID === activeRequest) inFlightKey = '';
    });
  return inFlight;
}

/** resetRealmProfileState 清理内存态，供退出应用或隔离测试重新初始化。 */
export function resetRealmProfileState(requestedRealm = getEnv().realm): void {
  activeRequest += 1;
  inFlight = undefined;
  inFlightKey = '';
  setRealmProfileState(createInitialRealmProfileState(requestedRealm));
}
